import { registerLocaleData } from "@angular/common";
import localeEn from "@angular/common/locales/en";
import localeFr from "@angular/common/locales/fr";
import { Component, DestroyRef, inject, OnInit } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Auth, browserLocalPersistence } from "@angular/fire/auth";
import { MatIconRegistry } from "@angular/material/icon";
import { DomSanitizer } from "@angular/platform-browser";
import { ActivatedRoute, RouterOutlet } from "@angular/router";
import { RouterQuery } from "@datorama/akita-ng-router-store";
import { TranslateService } from "@ngx-translate/core";
import { NiceTranslationFormService } from "@recursyve/nice-ui-kit.v2";
import { distinctUntilChanged, skip } from "rxjs/operators";

import { LanguagesEnum, languagesValues } from "./api/accounts/enums/languages.enum";
import { NavigationService } from "./navigation/navigation.service";
import { I18nService } from "./providers/i18n.service";
import { AppService } from "./store/app.service";

@Component({
    selector: "fmp-app",
    imports: [RouterOutlet],
    templateUrl: "./app.template.html",
    styleUrls: ["./app.style.scss"]
})
export class AppComponent implements OnInit {
    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly appService = inject(AppService);
    private readonly auth = inject(Auth);
    private readonly destroyRef = inject(DestroyRef);
    private readonly i18nService = inject(I18nService);
    private readonly matIconRegistry = inject(MatIconRegistry);
    private readonly navigationService = inject(NavigationService);
    private readonly routerQuery = inject(RouterQuery);
    private readonly sanitizer = inject(DomSanitizer);
    private readonly translateService = inject(TranslateService);
    private readonly translationFormService = inject(NiceTranslationFormService);

    constructor() {
        this.matIconRegistry.setDefaultFontSetClass("material-icons-outlined");
        this.matIconRegistry.addSvgIconSetInNamespace(
            "heroicons_outline",
            this.sanitizer.bypassSecurityTrustResourceUrl("assets/icons/heroicons-outline.svg")
        );
        this.matIconRegistry.addSvgIconSetInNamespace(
            "heroicons_solid",
            this.sanitizer.bypassSecurityTrustResourceUrl("assets/icons/heroicons-solid.svg")
        );
        this.matIconRegistry.addSvgIconSetInNamespace(
            "streamline-regular",
            this.sanitizer.bypassSecurityTrustResourceUrl("assets/icons/regular-icons.svg")
        );
        this.matIconRegistry.addSvgIconSetInNamespace(
            "streamline-solid",
            this.sanitizer.bypassSecurityTrustResourceUrl("assets/icons/solid-icons.svg")
        );
    }

    public async ngOnInit(): Promise<void> {
        await this.auth.setPersistence(browserLocalPersistence);

        this.appService.loadServerState().subscribe();

        await this.navigationService.init();

        const lang = this.i18nService.getDefaultLang();
        localStorage.setItem("lang", lang);
        this.updateLang(lang);
        this.setTranslationService(lang);

        this.activatedRoute.queryParams.pipe(skip(1), takeUntilDestroyed(this.destroyRef)).subscribe((queryParams) => {
            this.appService.appendQueryParams(queryParams);
        });

        this.routerQuery
            .select()
            .pipe(
                distinctUntilChanged(
                    (previous, current) => previous.state?.url.split("?")[0] === current.state?.url.split("?")[0]
                ),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((routerState) => {
                this.appService.appendRouterState(routerState);
            });
    }

    private updateLang(lang: string): void {
        this.translateService.setDefaultLang(lang);
        registerLocaleData(localeFr);
        registerLocaleData(localeEn);
    }

    private setTranslationService(requiredLang: LanguagesEnum): void {
        this.translationFormService.setLanguages([
            requiredLang,
            ...languagesValues.filter((language) => language !== requiredLang)
        ]);

        this.translationFormService.setRequiredLanguages(languagesValues);
    }
}
